// The color of a person's name (#540, #631, #698): a hex from one palette, the same in every theme and Vibe, in every server, for every
// viewer, on every client. Nothing here depends on the theme.
// - Stored: the server keeps a hex for every person (`user_settings.name_color_hex`). Durable and immutable: it is set once, to the
//   person's hash color, when they join their first server (or by migration 0027 for people who were here before), and after that only
//   the person's own pick in Settings changes it (PUT /api/me/settings; "Automatic" stores the hash color again). A change to the hash,
//   the palette, a theme or a Vibe never moves anyone. The server sends the stored hex in `Member.nameColor` and
//   `MemberProfile.nameColor`, and a client draws it. For a person not in the member list (left the server, a DM) the client uses the
//   hash color.
// - The palette is what a person can pick, and the only hexes the server accepts. It has a bold row, then an assorted grid; the picker
//   shows them in that order, then "Automatic". It may grow later: a stored hex stays valid, and the server draws any #rrggbb it holds.
// - The hash (`nameColorHash`) chooses only the FIRST color, from `NAME_AUTO`: mid-tones that reach 3:1 on every theme's message
//   backgrounds, so nobody is given a color they cannot read. A bold color such as pure blue is under 3:1 on some themes: that is the
//   picker's choice, and the picker notes it (`NAME_LOW_CONTRAST`, generated by scripts/gen-name-colors.mjs).
// The cases are shared/cases/name-color.json, which the Swift and Kotlin ports run too. The IRC layout's nick colors (`NICK_COLORS`) are
// separate and stay as they were.

/** The picker's top row: bold, clean colors. No white or black: each disappears on half the themes. */
export const NAME_BOLD: readonly string[] = [
  "#e6262a", // red
  "#ff8a00", // orange
  "#ffd60a", // yellow
  "#22b14c", // green
  "#00bcd4", // cyan
  "#1565ff", // blue
  "#e91ec4", // magenta
  "#8e30e8", // purple
  "#8b5a2b", // brown
  "#8a8a8a", // gray
];

/** The colors Automatic chooses from (`nameColorHash`): 16 mid-tones in farthest-point order, each at least 3:1 on every theme's message backgrounds. */
export const NAME_AUTO: readonly string[] = [
  "#457cfd", "#e9500a", "#009a1e", "#cc54c5", "#438691", "#99781e", "#a9768d", "#de3e79",
  "#9360ea", "#7177bf", "#008cce", "#00946b", "#6b8700", "#c66e00", "#d45e61", "#a362be",
];

/** The picker's grid: the 16 mid-tones, then 4 lighter and 4 darker shades. */
export const NAME_ASSORTED: readonly string[] = [
  ...NAME_AUTO,
  "#ffc3bd", "#d7da94", "#88e7ec", "#d3cdff", // lighter
  "#653e00", "#005725", "#004d74", "#6a2a66", // darker
];

/** Every color a person can pick, in the picker's order. */
export const NAME_PALETTE: readonly string[] = [...NAME_BOLD, ...NAME_ASSORTED];

const HEX = /^#[0-9a-f]{6}$/;

/** A stored color as `#rrggbb` in lower case, or null when it is not a hex. Any #rrggbb counts: the palette may have grown or changed. */
export function normalizeNameColor(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const lower = v.toLowerCase();
  return HEX.test(lower) ? lower : null;
}

/** A name color a person can pick: a hex of the palette (any case). Never a free color (#631). */
export function isNameColor(v: unknown): v is string {
  const hex = normalizeNameColor(v);
  return hex !== null && NAME_PALETTE.includes(hex);
}

/**
 * The first color of a person, from a hash of their id: h = h × 31 + each UTF-16 unit of the id, unsigned 32-bit, then h mod 16, an index
 * into `NAME_AUTO`. The hash is `nickIndex`'s (web/src/components/irc/ircLogic.ts, the avatar's `colorFor`, iOS `Theme.avatarIndex`).
 */
export function nameColorHash(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return NAME_AUTO[h % NAME_AUTO.length]!;
}

/** The color to draw for a person: their stored hex when it is a #rrggbb, else the hash color of their id. */
export function nameColorOf(id: string, stored: unknown): string {
  return normalizeNameColor(stored) ?? nameColorHash(id);
}
